/**
 * The frame every /onboarding screen shares: the /demo look (black, warm glow
 * from below, logo top-left), an optional progress bar, and the demo's price
 * card carried along when the visitor came from /demo.
 *
 * @module web/screens/onboarding/Shell
 */
import type { ReactNode } from "react";
import { STORAGE_KEY } from "../demo/intake.ts";
import "../../demo.css";
import "../../onboarding.css";

export interface DemoQuote { title: string; totalCents: number }

/** What /demo left behind (localStorage "aditor.demo.v1"), or null. Display only. */
export function readDemoQuote(storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage): DemoQuote | null {
  try {
    const q = JSON.parse(storage?.getItem(STORAGE_KEY) ?? "null")?.quote;
    if (!q || typeof q.totalCents !== "number" || q.totalCents <= 0) return null;
    const host = typeof q.productUrl === "string" && q.productUrl ? q.productUrl.replace(/^https?:\/\/(www\.)?/, "").split("/")[0] : "";
    const firstLine = typeof q.raw === "string" ? (q.raw.split(/\n/).find((l: string) => l.trim()) ?? "").trim() : "";
    return { title: (host || firstLine || "Your first ad").slice(0, 42), totalCents: q.totalCents };
  } catch {
    return null;
  }
}

/** The priced script itself, for the first brief. Read in render, forgotten in an effect (StrictMode-safe). */
export function readDemoBrief(storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage): string {
  try {
    const raw = JSON.parse(storage?.getItem(STORAGE_KEY) ?? "null")?.quote?.raw;
    return typeof raw === "string" ? raw : "";
  } catch {
    return "";
  }
}
export function forgetDemoBrief(storage: Storage | undefined = globalThis.localStorage): void {
  try {
    const saved = JSON.parse(storage?.getItem(STORAGE_KEY) ?? "null");
    if (saved?.quote) storage!.setItem(STORAGE_KEY, JSON.stringify({ ...saved, quote: null }));
  } catch { /* private mode */ }
}

const eur = (cents: number) => new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(cents / 100));

export function DemoQuoteCard({ quote }: { quote: DemoQuote | null }) {
  if (!quote) return null;
  return (
    <div className="ob2-quote" data-testid="ob-demo-quote">
      <img src="/aditor-logo.png" alt="" />
      <div><b>Your brief</b><span>{quote.title}</span></div>
      <strong>{eur(quote.totalCents)}</strong>
    </div>
  );
}

/** `step`/`of` draw the bar; leave them out on screens that are not part of the count. */
export function Shell({ step, of, screen, children }: { step?: number; of?: number; screen: string; children: ReactNode }) {
  return (
    <div className="dm dm-concept-meadow ob2" data-screen={screen}>
      <div className="dm-glow" aria-hidden="true" />
      <header className="ob2-head">
        <img src="/aditor-logo.png" alt="Aditor" className="dm-logo" />
        {step !== undefined && of !== undefined && (
          <div className="ob2-steps" role="progressbar" aria-valuemin={1} aria-valuemax={of} aria-valuenow={step} aria-label={`Step ${step} of ${of}`}>
            {Array.from({ length: of }, (_, i) => <i key={i} className={i < step ? "is-on" : ""} />)}
          </div>
        )}
        <span className="ob2-head-pad" />
      </header>
      <main className="ob2-main">{children}</main>
    </div>
  );
}
